import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar,
  Clock,
  Timer,
  AlertTriangle,
  CheckCircle,
  Hourglass,
} from "lucide-react";
import { firebaseApp } from "../../firebase";
import { getAuth } from "firebase/auth";
import { getFirestore, doc, setDoc, Timestamp } from "firebase/firestore";
import toast from "react-hot-toast";
import { sendEmail } from "../../utils/emailService";
import { safeToDate } from "../../utils/SafeToDate";

const SkillsValidationScheduler = ({
  user,
  profileData,
  deadline,
  schedule,
  pendingTestId,
  onTestLaunch,
  onScheduleUpdate,
}) => {
  const navigate = useNavigate();
  const db = getFirestore(firebaseApp);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedTodayTime, setSelectedTodayTime] = useState("");
  const [selectedTomorrowTime, setSelectedTomorrowTime] = useState("");
  const [localSchedule, setLocalSchedule] = useState(schedule || null);
  const [localPendingTestId, setLocalPendingTestId] = useState(pendingTestId || null);
  const [launching, setLaunching] = useState(false);
  const [now, setNow] = useState(new Date());
  const [creatingOverlay, setCreatingOverlay] = useState(false);

  useEffect(() => {
    setLocalSchedule(schedule || null);
  }, [schedule]);

  useEffect(() => {
    setLocalPendingTestId(pendingTestId || null);
  }, [pendingTestId]);

  const deadlineText = useMemo(() => {
    if (!deadline) return "48 hours";
    return deadline.toLocaleString();
  }, [deadline]);

  const deadlineExceeded = useMemo(() => {
    if (!deadline) return false;
    return new Date() > deadline;
  }, [deadline]);

  const timeLeft = useMemo(() => {
    if (!deadline) return "~48 hours";
    const diff = deadline.getTime() - now.getTime();
    if (diff <= 0) return "Expired";
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m left`;
  }, [deadline, now]);

  const ensureResumeData = () => {
    if (!profileData?.resumeData) {
      toast.error("Upload your resume before scheduling the test.");
      navigate("/job-seeker/resume-upload");
      return false;
    }
    return true;
  };

  const buildSkillProfile = () => {
    const resumeData = profileData?.resumeData || {};
    const skills =
      resumeData.professionalInfo?.skills || resumeData.skills || [];
    const experience =
      resumeData.professionalInfo?.experience || resumeData.experience || "";
    const workHistory = resumeData.workHistory || [];
    const summary =
      resumeData.professionalInfo?.summary || resumeData.summary || "";

    return {
      primarySkills: Array.isArray(skills) ? skills.slice(0, 10) : [],
      experience,
      recentRole: workHistory[0]?.title || "Professional",
      yearsOfExperience: extractYearsFromExperience(experience),
      domain: determineDomain(skills, workHistory),
      summary,
    };
  };

  const extractYearsFromExperience = (experience) => {
    if (Array.isArray(experience)) {
      if (experience.length > 0) {
        return `${experience.length}+ years`;
      }
      return "Not specified";
    }

    if (typeof experience === "string") {
      const match = experience.match(/(\d+)[\+\-]?\s*(year|yr)/i);
      return match ? `${match[1]} years` : "Not specified";
    }

    return "Not specified";
  };

  const determineDomain = (skills, workHistory) => {
    const techKeywords = [
      "javascript",
      "python",
      "react",
      "node",
      "java",
      "programming",
      "software",
      "developer",
    ];
    const marketingKeywords = [
      "marketing",
      "seo",
      "social media",
      "content",
      "advertising",
    ];
    const designKeywords = [
      "design",
      "ui",
      "ux",
      "photoshop",
      "figma",
      "creative",
    ];
    const dataKeywords = [
      "data",
      "analytics",
      "sql",
      "tableau",
      "analysis",
      "science",
    ];

    const allText = [
      ...(Array.isArray(skills) ? skills : []),
      ...(Array.isArray(workHistory)
        ? workHistory.map((w) => `${w.title || ""} ${w.description || ""}`)
        : []),
    ]
      .join(" ")
      .toLowerCase();

    if (techKeywords.some((keyword) => allText.includes(keyword))) return "Technology";
    if (marketingKeywords.some((keyword) => allText.includes(keyword))) return "Marketing";
    if (designKeywords.some((keyword) => allText.includes(keyword))) return "Design";
    if (dataKeywords.some((keyword) => allText.includes(keyword))) return "Data & Analytics";
    return "General";
  };

  const parseTime = (timeString) => {
    if (!timeString) return null;
    const [hours, minutes] = timeString.split(":").map((val) => parseInt(val, 10));
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
    return { hours, minutes };
  };

  const buildDateTime = (baseDate, timeString) => {
    const parsed = parseTime(timeString);
    if (!parsed) return null;
    const target = new Date(baseDate);
    target.setHours(parsed.hours, parsed.minutes, 0, 0);
    return target;
  };

  const createOrScheduleTest = async (option, scheduledDate) => {
    // If a pending test already exists and user chose to start now,
    // jump straight into the existing test without re-generating.
    if (option === "now" && localPendingTestId) {
      await launchTest(localPendingTestId, localSchedule);
      return localPendingTestId;
    }
    // Show a loading overlay while generating the initial test.
    if (option === "now" && !localPendingTestId) {
      setCreatingOverlay(true);
    }
    if (!user || !ensureResumeData()) return null;
    setIsSaving(true);
    try {
      const auth = getAuth(firebaseApp);
      const currentUser = auth.currentUser;
      if (!currentUser) {
        toast.error("Please sign in again to schedule your test.");
        return null;
      }

      const idToken = await currentUser.getIdToken();
      const skillProfile = buildSkillProfile();

      const response = await fetch(
        "https://us-central1-vetriaai-mvp.cloudfunctions.net/generateSkillsTest",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            skills: skillProfile.primarySkills,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `API error: ${response.status}`);
      }

      const testData = await response.json();
      const numberOfQuestions = testData.questions?.length || 15;
      const durationInMinutes = Math.ceil((numberOfQuestions * 45) / 60);
      const testId = `${user.id}_${Date.now()}`;

      const schedulePayload = scheduledDate
        ? {
          status: "scheduled",
          option,
          scheduledAt: Timestamp.fromDate(scheduledDate),
          createdAt: Timestamp.now(),
        }
        : {
          status: "ready",
          option,
          scheduledAt: Timestamp.now(),
        };

      await setDoc(doc(db, "skillsValidationTests", testId), {
        jobSeekerId: user.id,
        jobSeekerName: user.name,
        jobSeekerEmail: user.email,
        testType: "skills_validation",
        title:
          testData.title ||
          `Skills Validation Test - ${skillProfile.primarySkills.slice(0, 3).join(", ")}`,
        description:
          testData.description ||
          `Test your skills in ${skillProfile.primarySkills.slice(0, 5).join(", ")} and more`,
        duration: durationInMinutes,
        questions: testData.questions,
        skillProfile,
        generatedAt: new Date(),
        resumeBasedTest: true,
        status: scheduledDate ? "scheduled" : "active",
        scheduledStartAt: scheduledDate ? Timestamp.fromDate(scheduledDate) : null,
        scheduleOption: option,
      });

      await setDoc(
        doc(db, "userProfiles", user.id),
        {
          pendingSkillsValidationTestId: testId,
          skillsValidationSchedule: schedulePayload,
          ...(deadline
            ? { skillsValidationDeadline: Timestamp.fromDate(deadline) }
            : {}),
        },
        { merge: true }
      );

      setLocalSchedule(schedulePayload);
      setLocalPendingTestId(testId);
      onScheduleUpdate?.({ schedule: schedulePayload, pendingTestId: testId });

      if (!scheduledDate) {
        // Launch test immediately without toast delay
        await launchTest(testId, schedulePayload);
        return testId;
      } else {
        toast.success("Skill test scheduled. We will start it at the selected time.");
        // Fire an email confirmation for the scheduled test
        try {
          const dtStr = scheduledDate.toLocaleString();
          await sendEmail({
            to: user.email,
            subject: "Your skills validation test is scheduled",
            html: `
              <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
                <h1 style="color: #991b1b;">Skills Validation Scheduled</h1>
                <p>Hi ${user.name || "there"},</p>
                <p>Your skills validation test is scheduled for <strong>${dtStr}</strong>.</p>
                <p>You must complete it within 48 hours of registration to unlock your dashboard.</p>
                <a href="https://vetriaai.com/job-seeker/take-test/${testId}"
                   style="display:inline-block;background-color:#991b1b;color:#fff;padding:12px 20px;text-decoration:none;border-radius:6px;margin-top:16px;">Start Test</a>
              </div>
            `,
            text: `Your skills validation test is scheduled for ${dtStr}. Start: https://vetriaai.com/job-seeker/take-test/${testId}`,
          });
        } catch (e) {
          console.warn("Failed to send schedule email:", e);
        }
      }

      return testId;
    } catch (error) {
      console.error("Failed to create skills validation test:", error);
      toast.error(error.message || "Unable to create the test right now.");
      return null;
    } finally {
      setCreatingOverlay(false);
      setIsSaving(false);
    }
  };

  const launchTest = async (testId, schedulePayload) => {
    if (!testId || launching) return;
    setLaunching(true);
    try {
      if (schedulePayload) {
        await setDoc(
          doc(db, "userProfiles", user.id),
          {
            skillsValidationSchedule: {
              ...schedulePayload,
              status: "in_progress",
              startedAt: Timestamp.now(),
            },
          },
          { merge: true }
        );
      }

      if (onTestLaunch) {
        onTestLaunch(testId);
      } else {
        navigate(`/job-seeker/take-test/${testId}`, {
          state: {
            message: "Complete your skills validation test to unlock the dashboard.",
          },
        });
      }
    } finally {
      setLaunching(false);
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      if (!localSchedule?.scheduledAt || !localPendingTestId) return;
      const scheduledDate = safeToDate(localSchedule.scheduledAt);
      if (!scheduledDate) return;
      const nowTs = new Date();
      setNow(nowTs);
      if (nowTs >= scheduledDate) {
        launchTest(localPendingTestId, localSchedule);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [localSchedule, localPendingTestId]);

  useEffect(() => {
    const tick = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(tick);
  }, []);

  const handleScheduleToday = async () => {
    if (deadlineExceeded) {
      toast.error("Deadline passed. Start the test now to continue.");
      return;
    }
    const target = buildDateTime(new Date(), selectedTodayTime);
    if (!target) {
      toast.error("Pick a valid time for today.");
      return;
    }
    if (target.getTime() <= Date.now()) {
      toast.error("Please select a future time today.");
      return;
    }
    await createOrScheduleTest("today", target);
  };

  const handleScheduleTomorrow = async () => {
    if (deadlineExceeded) {
      toast.error("Deadline passed. Start the test now to continue.");
      return;
    }
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const target = buildDateTime(tomorrow, selectedTomorrowTime);
    if (!target) {
      toast.error("Pick a valid time for tomorrow.");
      return;
    }
    await createOrScheduleTest("tomorrow", target);
  };

  return (
    <div className="max-w-4xl mx-auto">
      {creatingOverlay && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-xl p-5 flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-red-700 border-t-transparent rounded-full animate-spin"></div>
            <div>
              <p className="text-sm font-semibold text-gray-900">Preparing your test…</p>
              <p className="text-xs text-gray-600">Hang tight, launching shortly.</p>
            </div>
          </div>
        </div>
      )}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 mt-6">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-full bg-red-50 border border-red-100">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <div className="flex-1">
            {profileData?.lastSkillsValidationScore !== undefined && !profileData?.skillsVerified && profileData?.lastSkillsValidationScore < 55 ? (
              <>
                <h2 className="text-xl font-semibold text-red-700 mb-1">Validation Failed</h2>
                <p className="text-gray-700 mb-2">
                  You scored <span className="font-bold">{profileData.lastSkillsValidationScore}%</span> on your last attempt.
                  You need <span className="font-bold">55%</span> to pass. Please retake the test to unlock your dashboard.
                </p>
              </>
            ) : (
              <>
                <h2 className="text-xl font-semibold text-gray-900 mb-1">Finish your skills validation</h2>
                <p className="text-gray-700 mb-2">
                  Upload is complete. You need to finish the skills validation test before you can use the dashboard.
                </p>
              </>
            )}
            <p className="text-sm text-gray-600">
              Deadline: <span className="font-semibold text-gray-900">{deadlineText}</span>
              {deadlineExceeded && (
                <span className="block text-red-700 font-semibold mt-1">Deadline exceeded. You must start the test now.</span>
              )}
            </p>
            {!deadlineExceeded && (
              <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-50 border border-yellow-200 text-sm text-yellow-800">
                <Hourglass className="w-4 h-4" />
                <span>{timeLeft}</span>
              </div>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mt-6">
          <button
            onClick={() => createOrScheduleTest("now")}
            disabled={isSaving}
            className="w-full p-4 rounded-lg border border-red-200 bg-red-600 text-white font-semibold hover:bg-red-700 transition-colors disabled:opacity-60"
          >
            <div className="flex items-center justify-center gap-2">
              <Timer className="w-5 h-5" />
              <span>Take Test Now</span>
            </div>
            <p className="text-sm mt-1 text-red-50">Start immediately and unlock access sooner.</p>
          </button>

          <div className="p-4 rounded-lg border border-gray-200 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-5 h-5 text-gray-700" />
              <span className="font-semibold text-gray-900">Schedule for Today</span>
            </div>
            <input
              type="time"
              value={selectedTodayTime}
              onChange={(e) => setSelectedTodayTime(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
            <button
              onClick={handleScheduleToday}
              disabled={isSaving || deadlineExceeded}
              className="mt-2 w-full px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-black transition-colors disabled:opacity-60"
            >
              Set for Today
            </button>
          </div>

          <div className="p-4 rounded-lg border border-gray-200 bg-gray-50">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-5 h-5 text-gray-700" />
              <span className="font-semibold text-gray-900">Schedule for Tomorrow</span>
            </div>
            <input
              type="time"
              value={selectedTomorrowTime}
              onChange={(e) => setSelectedTomorrowTime(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
            <button
              onClick={handleScheduleTomorrow}
              disabled={isSaving || deadlineExceeded}
              className="mt-2 w-full px-4 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-60"
            >
              Set for Tomorrow
            </button>
          </div>
        </div>

        {localSchedule && (
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-blue-900">Test scheduled</p>
              <p className="text-sm text-blue-800">
                Status: {localSchedule.status} · Scheduled time:{" "}
                {safeToDate(localSchedule.scheduledAt)?.toLocaleString() || "Now"}
              </p>
              {localPendingTestId && (
                <button
                  onClick={() => launchTest(localPendingTestId, localSchedule)}
                  disabled={launching}
                  className="mt-3 inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-60"
                >
                  <Timer className="w-4 h-4" />
                  Start Scheduled Test
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SkillsValidationScheduler;
